import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { HelpPopupInline } from '@bingads-webui-react/help-popup';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import {
  TextField, PrimaryButton, DefaultButton, IconButton, MessageBar, MessageBarType,
} from '@bingads-webui-react/fabric-control';

import { getBasicItemStyle } from './style';

export const TextItem = ({
  text,
  title,
  errorMessage,
  showHelpPopup,
  helpTopic,
  updateText,
  i18n,
  ariaLabel,
}) => {
  const classes = useDefaultStyles(getBasicItemStyle);

  return (
    <div className={classNames(classes.textContainer, 'text-container')}>
      <div className="item-title">
        <div className="text-title">{title || i18n.getString(_TL_('Text'))}</div>
        {showHelpPopup && <HelpPopupInline topic={helpTopic} />}
      </div>
      <TextField
        value={text}
        errorMessage={errorMessage}
        onChange={(e, str) => updateText(str)}
        className="text-field"
        ariaLabel={ariaLabel}
      />
    </div>
  );
};

TextItem.propTypes = {
  text: PropTypes.string.isRequired,
  updateText: PropTypes.func.isRequired,
  title: PropTypes.string,
  errorMessage: PropTypes.string,
  showHelpPopup: PropTypes.bool,
  helpTopic: PropTypes.string,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }),
  ariaLabel: PropTypes.string,
};

TextItem.defaultProps = {
  title: '',
  errorMessage: '',
  showHelpPopup: false,
  helpTopic: '',
  i18n: {
    getString: () => '',
  },
  ariaLabel: '',
};

export const ButtonContainer = ({
  i18n,
  handleApply,
  handleCancel,
  disableApply,
}) => {
  const classes = useDefaultStyles(getBasicItemStyle);

  return (
    <div className={classNames(classes.buttonContainer, 'button-container')}>
      <PrimaryButton
        text={i18n.getString(_TL_('Apply'))}
        onClick={handleApply}
        disabled={disableApply}
        className="apply-creative-button"
      />
      <DefaultButton
        text={i18n.getString(_TL_('Cancel'))}
        onClick={handleCancel}
        className="cancel-button"
      />
    </div>
  );
};

ButtonContainer.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  handleApply: PropTypes.func.isRequired,
  handleCancel: PropTypes.func.isRequired,
  disableApply: PropTypes.bool,
};

ButtonContainer.defaultProps = {
  disableApply: false,
};

export const ImageContainer = ({
  onLoadingImage,
  currentImage,
  isTestEnv,
  title,
  showHelpPopup,
  helpTopic,
  i18n,
  displayEdit,
  onEdit,
  displayDelete,
  onDelete,
}) => {
  const [showImageFailMsg, setShowImageFailMsg] = useState(false);

  const onImageLoad = useCallback((success) => {
    setShowImageFailMsg(!success);
    onLoadingImage(success);
  }, [onLoadingImage]);

  useEffect(() => {
    if (currentImage) {
      const image = new Image();
      image.src = currentImage;
      image.onload = () => {
        // if the actual image is 404, it will return a placeholder image which width and height are 80
        // todo: investigate if there is a better way to handle this
        if (image.width === 80 && image.height === 80 && !isTestEnv) {
          onImageLoad(false);
        } else {
          onImageLoad(true);
        }
      };
      image.onerror = () => {
        if (!isTestEnv) {
          onImageLoad(false);
        }
      };
    }
  }, [currentImage, isTestEnv, onImageLoad]);

  const classes = useDefaultStyles(getBasicItemStyle);

  return (
    <div className={classNames(classes.imageContainer, 'image-container')}>
      <div className="item-title">
        <div className="image-title">{title}</div>
        { showHelpPopup && <HelpPopupInline topic={helpTopic} /> }
      </div>
      {
          currentImage && (
            <div className="image-box">
              <img src={currentImage} alt="original" />
              {
                displayEdit &&
                <div className={classNames('edit-button-container', displayDelete && 'have-delete')}>
                  <IconButton
                    iconProps={{ iconName: 'Edit' }}
                    onClick={onEdit}
                    className="edit-image-button"
                  />
                </div>
                }
              {
                displayDelete &&
                <div className="delete-button-container">
                  <IconButton
                    iconProps={{ iconName: 'Delete' }}
                    onClick={onDelete}
                    className="delete-image-button"
                  />
                </div>
              }
            </div>
            )
          }
      {
            showImageFailMsg &&
            <MessageBar
              messageBarType={MessageBarType.error}
              isMultiline={false}
              className="image-load-fail-msg"
            >
              {i18n.getString(_TL_('We cannot find the image. Please select another image.'))}
            </MessageBar>
          }
    </div>
  );
};

ImageContainer.propTypes = {
  onLoadingImage: PropTypes.func.isRequired,
  currentImage: PropTypes.string.isRequired,
  isTestEnv: PropTypes.bool,
  title: PropTypes.string.isRequired,
  showHelpPopup: PropTypes.bool,
  helpTopic: PropTypes.string,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  displayEdit: PropTypes.bool.isRequired,
  onEdit: PropTypes.func.isRequired,
  displayDelete: PropTypes.bool,
  onDelete: PropTypes.func,
};

ImageContainer.defaultProps = {
  isTestEnv: false,
  showHelpPopup: false,
  helpTopic: '',
  displayDelete: false,
  onDelete: () => {},
};
